// Tabela de honorários do escritório: valores de referência por serviço, regra do êxito e PDF
import { useEffect, useMemo, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowDown, ArrowUp, Download, Loader2, Plus, Trash2, Trophy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatBRLFromDigits, numberToBRLInputFormat, parseBRLStringToNumber } from '@/utils/brlFormat';
import { downloadPdf } from '@/utils/reportPdf';

interface FeeItem {
  id: string;
  title: string;
  amount: number | null;
  notes: string | null;
  position: number;
}

const brl = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const FeeSchedule = () => {
  const { toast } = useToast();
  const [items, setItems] = useState<FeeItem[]>([]);
  const [year, setYear] = useState<number>(new Date().getFullYear() + 1);
  const [successFee, setSuccessFee] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [itemsRes, settingsRes] = await Promise.all([
      (supabase as any).from('fee_schedule').select('id, title, amount, notes, position').order('position'),
      (supabase as any).from('fee_schedule_settings').select('year, success_fee').maybeSingle(),
    ]);
    setItems(((itemsRes.data || []) as any[]).map((i) => ({ ...i, amount: i.amount === null ? null : Number(i.amount) })));
    if (settingsRes.data) {
      if (settingsRes.data.year) setYear(settingsRes.data.year);
      setSuccessFee(settingsRes.data.success_fee || '');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const saveItem = async (item: FeeItem, patch: Partial<FeeItem>) => {
    setSavingId(item.id);
    const { error } = await (supabase as any)
      .from('fee_schedule')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', item.id);
    setSavingId(null);
    if (error) toast({ title: 'Não foi possível salvar', description: error.message, variant: 'destructive' });
  };

  const updateLocal = (id: string, patch: Partial<FeeItem>) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const addItem = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const position = items.length ? Math.max(...items.map((i) => i.position)) + 1 : 0;
    const { data, error } = await (supabase as any)
      .from('fee_schedule')
      .insert({ user_id: user.id, title: 'Novo serviço', amount: null, position })
      .select('id, title, amount, notes, position')
      .single();
    if (error) {
      toast({ title: 'Não foi possível adicionar', description: error.message, variant: 'destructive' });
      return;
    }
    setItems((prev) => [...prev, { ...data, amount: null }]);
  };

  const removeItem = async (item: FeeItem) => {
    if (!window.confirm(`Excluir "${item.title}" da tabela?`)) return;
    const { error } = await (supabase as any).from('fee_schedule').delete().eq('id', item.id);
    if (error) {
      toast({ title: 'Não foi possível excluir', description: error.message, variant: 'destructive' });
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((i, pos) => ({ ...i, position: pos }));
    setItems(renumbered);
    await Promise.all([renumbered[index], renumbered[target]].map((i) => (supabase as any).from('fee_schedule').update({ position: i.position }).eq('id', i.id)));
  };

  const saveSettings = async (patch: { year?: number; success_fee?: string }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await (supabase as any)
      .from('fee_schedule_settings')
      .upsert({ user_id: user.id, year, success_fee: successFee, ...patch, updated_at: new Date().toISOString() });
  };

  const stats = useMemo(() => {
    const priced = items.filter((i) => i.amount && i.amount > 0).map((i) => i.amount as number);
    return {
      total: items.length,
      priced: priced.length,
      average: priced.length ? priced.reduce((a, b) => a + b, 0) / priced.length : 0,
      min: priced.length ? Math.min(...priced) : 0,
      max: priced.length ? Math.max(...priced) : 0,
    };
  }, [items]);

  // PDF no estilo da tabela do escritório
  const exportPdf = async () => {
    setExporting(true);
    try {
      const pdf = await PDFDocument.create();
      const page = pdf.addPage([595.28, 841.89]);
      const regular = await pdf.embedFont(StandardFonts.Helvetica);
      const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
      const GOLD = rgb(0.769, 0.671, 0.514);
      const GOLD_DARK = rgb(0.604, 0.498, 0.322);
      const INK = rgb(0.059, 0.09, 0.165);
      const LINE = rgb(0.85, 0.82, 0.77);
      const W = 595.28;
      const safe = (t: string) => t.split('').filter((ch) => { try { regular.encodeText(ch); return true; } catch { return false; } }).join('');
      const center = (text: string, y: number, size: number, font = bold, color = INK) => {
        const s = safe(text);
        page.drawText(s, { x: (W - font.widthOfTextAtSize(s, size)) / 2, y, size, font, color });
      };

      try {
        const bytes = await fetch(encodeURI('/imgs/logos allana/letra_A_logo.png')).then((r) => r.arrayBuffer());
        const logo = await pdf.embedPng(bytes);
        const wm = 380;
        page.drawImage(logo, { x: (W - (logo.width / logo.height) * wm) / 2, y: 170, width: (logo.width / logo.height) * wm, height: wm, opacity: 0.07 });
        page.drawImage(logo, { x: 120, y: 742, width: (logo.width / logo.height) * 56, height: 56 });
      } catch {
        // sem logo, segue só com o texto
      }
      page.drawText('ALLANA STHEFANY', { x: 185, y: 772, size: 22, font: regular, color: GOLD });
      page.drawText('ADVOCACIA E CONSULTORIA JURÍDICA', { x: 186, y: 756, size: 7.5, font: regular, color: GOLD_DARK });
      center('TABELA HONORÁRIOS', 700, 15);
      center(String(year), 680, 15);

      const x0 = 60;
      const x1 = 420;
      const x2 = W - 60;
      let y = 650;
      const rowH = 20;
      const rows = items.length ? items : [];
      rows.forEach((item, i) => {
        if (i % 2 === 1) page.drawRectangle({ x: x0, y: y - rowH, width: x2 - x0, height: rowH, color: rgb(0.985, 0.975, 0.96) });
        page.drawLine({ start: { x: x0, y }, end: { x: x2, y }, thickness: 0.6, color: LINE });
        page.drawText(safe(item.title), { x: x0 + 8, y: y - 14, size: 9.5, font: bold, color: INK });
        page.drawText(item.amount ? safe(brl(item.amount).replace(/ /g, ' ')) : 'R$', { x: x1 + 8, y: y - 14, size: 9.5, font: bold, color: INK });
        y -= rowH;
      });
      page.drawLine({ start: { x: x0, y }, end: { x: x2, y }, thickness: 0.6, color: LINE });
      page.drawLine({ start: { x: x0, y: 650 }, end: { x: x0, y }, thickness: 0.6, color: LINE });
      page.drawLine({ start: { x: x1, y: 650 }, end: { x: x1, y }, thickness: 0.6, color: LINE });
      page.drawLine({ start: { x: x2, y: 650 }, end: { x: x2, y }, thickness: 0.6, color: LINE });

      if (successFee.trim()) {
        y -= 30;
        const label = 'NO ÊXITO - ';
        page.drawText(safe(label), { x: x0 + 20, y, size: 10, font: bold, color: INK });
        page.drawText(safe(successFee.toUpperCase()).slice(0, 70), { x: x0 + 20 + bold.widthOfTextAtSize(safe(label), 10), y, size: 10, font: regular, color: INK });
      }

      page.drawRectangle({ x: (W - 150) / 2, y: 40, width: 150, height: 24, color: GOLD });
      center('@allanasthefany.adv', 48, 9.5, regular, rgb(1, 1, 1));

      downloadPdf(await pdf.save(), `tabela-honorarios-${year}.pdf`);
    } catch (error: any) {
      toast({ title: 'Erro ao gerar o PDF', description: error?.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 p-4 md:p-6">
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Escritório</p>
          <h1 className="mt-1 flex items-baseline gap-2 text-3xl font-semibold tracking-tight text-slate-900">
            Honorários
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || year)}
              onBlur={() => saveSettings({ year })}
              className="w-20 rounded-lg bg-transparent text-2xl font-semibold text-[#9a7f52] outline-none focus:bg-[#faf7f2]"
              title="Ano da tabela"
            />
          </h1>
          <p className="mt-1 text-sm text-slate-500">Sua tabela de referência para manter os valores sempre alinhados.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf} disabled={exporting || loading} className="h-10 rounded-xl border-slate-200 text-[13px] shadow-none">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4 text-[#9a7f52]" />}
            Baixar PDF
          </Button>
          <Button onClick={addItem} className="h-10 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800">
            <Plus className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
            Novo serviço
          </Button>
        </div>
      </header>

      {/* Tabela */}
      <section className="overflow-hidden rounded-2xl border border-[#c4ab83]/40 bg-white shadow-[0_12px_32px_-20px_rgba(122,100,64,0.45)]">
        <div className="grid grid-cols-[1fr_200px_72px] gap-3 border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a7f52]">
          <span>Serviço</span>
          <span className="text-right">Valor</span>
          <span />
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#c4ab83]" /></div>
        ) : items.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-slate-400">Nenhum serviço ainda. Use "Novo serviço".</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item, index) => (
              <li key={item.id} className="group px-6 py-3.5 transition-colors hover:bg-[#faf7f2]/60">
                <div className="grid grid-cols-[1fr_200px_72px] items-center gap-3">
                  <input
                    value={item.title}
                    onChange={(e) => updateLocal(item.id, { title: e.target.value })}
                    onBlur={(e) => saveItem(item, { title: e.target.value.trim() || 'Serviço' })}
                    className="w-full rounded-lg bg-transparent px-2 py-1.5 text-base font-semibold text-slate-900 outline-none focus:bg-white focus:ring-2 focus:ring-[#c4ab83]/30"
                  />
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#9a7f52]">R$</span>
                    <input
                      inputMode="numeric"
                      placeholder="a definir"
                      value={item.amount ? numberToBRLInputFormat(item.amount) : ''}
                      onChange={(e) => {
                        const value = parseBRLStringToNumber(formatBRLFromDigits(e.target.value));
                        updateLocal(item.id, { amount: value || null });
                      }}
                      onBlur={() => saveItem(item, { amount: items.find((i) => i.id === item.id)?.amount ?? null })}
                      className={`w-full rounded-xl py-2 pl-10 pr-3 text-right text-lg tabular-nums outline-none transition-colors focus:bg-white focus:ring-2 focus:ring-[#c4ab83]/40 ${item.amount ? 'bg-[#faf7f2] font-semibold text-slate-900' : 'bg-slate-50 text-slate-300 placeholder:text-slate-300'}`}
                    />
                  </div>
                  <div className="flex justify-end gap-0.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    {savingId === item.id ? (
                      <Loader2 className="m-1.5 h-3.5 w-3.5 animate-spin text-[#c4ab83]" />
                    ) : (
                      <>
                        <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="rounded-md p-1 text-slate-300 hover:bg-white hover:text-slate-600 disabled:opacity-30" title="Subir">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => move(index, 1)} disabled={index === items.length - 1} className="rounded-md p-1 text-slate-300 hover:bg-white hover:text-slate-600 disabled:opacity-30" title="Descer">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => removeItem(item)} className="rounded-md p-1 text-slate-300 hover:bg-red-50 hover:text-red-600" title="Excluir">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <input
                  value={item.notes || ''}
                  placeholder="Observação (opcional): ex. parcelável em 3x, inclui recurso..."
                  onChange={(e) => updateLocal(item.id, { notes: e.target.value })}
                  onBlur={(e) => saveItem(item, { notes: e.target.value.trim() || null })}
                  className="mt-0.5 w-full rounded-md bg-transparent px-2 py-0.5 text-xs text-slate-500 outline-none placeholder:text-slate-300 focus:bg-white focus:ring-2 focus:ring-[#c4ab83]/20"
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Êxito */}
      <section className="relative overflow-hidden rounded-2xl bg-slate-900 p-6 text-white">
        <img src="/imgs/logos allana/letra_A_logo.png" alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-8 -right-6 h-40 w-40 select-none object-contain opacity-[0.12]" />
        <div className="relative flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#e8d5a8]" />
          <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-[#e8d5a8]">No êxito</p>
        </div>
        <Textarea
          value={successFee}
          onChange={(e) => setSuccessFee(e.target.value)}
          onBlur={() => saveSettings({ success_fee: successFee })}
          rows={2}
          placeholder="Ex.: 2 salários líquidos referentes ao cargo"
          className="relative mt-3 resize-none rounded-xl border-white/10 bg-white/5 text-lg font-semibold text-white shadow-none placeholder:text-white/30 focus-visible:ring-[#c4ab83]/40"
        />
        <p className="relative mt-2 text-[11px] text-white/50">Tudo é salvo automaticamente ao sair do campo.</p>
      </section>
    </div>
  );
};

export default FeeSchedule;
